import { useCallback, useState } from 'react';
import type { SetStateAction } from 'react';
import {
  Storage,
  StorageConflictError,
  StorageReadOnlyFieldError,
  StorageStaleError,
} from '../lib/storage';
import { errMsg } from '../lib/errors';
import { hasSavedBookingHours } from '../lib/bookingHours';
import {
  BOOKING_CONFIG_NOT_READ,
  BOOKING_GUEST_PRICES_NOT_READ,
  BOOKING_ZONES_NOT_READ,
  readBookingConfig,
  readBookingGuestPrices,
  readBookingZones,
} from '../lib/bookingConfigRead';
import type { BookingNotReadCode } from '../lib/bookingConfigRead';
import { BOOKING_DEFAULT_CONFIG } from '../engine';

// ─────────────────────────────────────────────────────────────────────
// useBookingSettings — the booking-module ADMIN settings (price zones +
// config), lifted out of the Padel24AppMain god component (see
// docs/history/DECOMPOSITION_PLAN.md, PR-C).
//
// These two slices are the lowest-coupling persisted state after clubInfo:
//   • bookingZones  — price zones (Storage key 'booking_zones_v1')
//   • bookingConfig — buffer rule, opening hours, durations, enabled courts,
//                     court metadata, … (Storage key 'booking_config_v1')
// Each has a SINGLE Storage-first mutator, NO blanket-persist effect, and NO
// §13.20 loaded-flag (so there is no "write before load" race). They are read
// by the booking engine (validation / slot generation) but written only here.
//
// Unlike useClubInfo (which owns its own load effect), the INITIAL load of
// these slices stays in the monolith's ATOMIC mount loader — it reads them
// alongside ~13 other domains in one pass and flushes them via setBookingZones
// / setBookingConfig (exposed below). Keeping the cross-slice loader atomic is
// a deliberate constraint (DECOMPOSITION_PLAN principle #1 / R1); when the
// loader later moves into StoreProvider it will call these same setters.
//
// The config default comes from the shared engine ('../engine')
// (Padel24Engine.booking.DEFAULT_CONFIG IS BOOKING_DEFAULT_CONFIG). The zones
// start EMPTY: there is no default zone (#1312), and a club with no saved zone
// has no court price — the screens say «Pris ikke satt».
// ─────────────────────────────────────────────────────────────────────

/** A booking price zone (loosely typed — the engine treats zones as `any`). */
export type BookingZone = any;
/** Booking-module configuration (loosely typed — the engine treats it as `any`). */
export type BookingConfig = any;
/**
 * The venue's guest prices that are not on a zone (#833): what a guest pays for
 * minutes no zone covers. Its own document, `booking_guest_prices_v1`, because
 * it is a PRICE and is written under `booking.rules.manage`, while the member
 * default sits in the venue setup (`court.manage`). `null` until loaded, or when
 * nothing is stored. The per-zone guest prices are on the zones themselves.
 */
export interface BookingGuestPrices {
  guestDefaultPricePerHour?: number | null;
}

/** Result of a settings save — mirrors the monolith's return shape exactly. */
export interface BookingSettingsUpdateResult {
  success: boolean;
  error?: string;
  /**
   * Set when the save was refused before anything was sent, because its
   * document has not been read (#1359, #1362).
   */
  code?: BookingNotReadCode;
  /**
   * What is KNOWN about a write that reached `Storage` and failed (#1370):
   *   `conflict` — the server refused it: the document moved since this client
   *                read it, somebody else saved meanwhile (a version 409);
   *   `refused`  — known NOT applied for another reason: refused locally before
   *                it was sent (a copy `Storage` knew to be stale), or a 409
   *                for a field the server owns;
   *   `unknown`  — anything else: a 5xx, a dropped connection, another status.
   *                The write MAY have landed (`Storage` says so itself), so
   *                nothing may claim it was not saved.
   */
  outcome?: BookingWriteOutcome;
}

/** See `BookingSettingsUpdateResult.outcome`. */
export type BookingWriteOutcome = 'conflict' | 'refused' | 'unknown';

/**
 * A save that reached `Storage` and failed (#1370): a 409, a 5xx, a dropped
 * connection, or a copy `Storage` already knew to be stale. Every one of them
 * leaves the key must-refresh in `Storage` (lib/storage.ts), so no save of that
 * document can go out until it is read again.
 *
 * Only the errors that PROVE the write was not applied are `conflict` or
 * `refused`; everything else is `unknown`, conservatively, because a 5xx or a
 * lost response may have come after the server committed.
 */
function writeFailed(e: unknown): BookingSettingsUpdateResult {
  const outcome: BookingWriteOutcome =
    e instanceof StorageConflictError
      ? 'conflict'
      : e instanceof StorageStaleError || e instanceof StorageReadOnlyFieldError
        ? 'refused'
        : 'unknown';
  return { success: false, error: errMsg(e), outcome };
}

export function useBookingSettings() {
  // No zones until the club saves some (#1312, owner decision 2026-11-03) —
  // never an invented one, whose price the server would not charge.
  const [bookingZones, setHeldBookingZones] = useState<BookingZone[]>(() => []);
  // WHETHER THE ZONES WERE READ (#1362). The empty list above is also what a
  // FAILED read leaves, and then `Storage` holds no version for the key: a save
  // would go out with no precondition and replace the club's real zones with a
  // list that started empty. False until a read answers (the zones, or that
  // there are none), and while false no zone save goes out.
  const [bookingZonesRead, setBookingZonesRead] = useState(false);
  // Replace the held zones. `read` is whether they came from a read that
  // ANSWERED — the loader and the re-read say so. Without it nothing is known
  // about what is stored (the member loader, which never writes them), and
  // saving is refused until a read answers.
  const setBookingZones = useCallback((next: SetStateAction<BookingZone[]>, read = false) => {
    setHeldBookingZones(next);
    setBookingZonesRead(read);
  }, []);
  // Default config (buffer rule, opening hours, etc.) merged on load.
  const [bookingConfig, setHeldBookingConfig] = useState<BookingConfig>(() => ({
    ...BOOKING_DEFAULT_CONFIG,
  }));
  // THE DOCUMENT AS STORED — the base a save merges into (#1357). The held copy
  // above is the loader's merge over the engine defaults, so it carries
  // 07:00–23:00 whether or not the club ever saved hours. Merging a save onto
  // THAT wrote the defaults as if somebody had chosen them, and a «Standardpris»
  // edit opened booking (#1192 says only saving the hours does). Null when
  // nobody said what is stored — before the loader answers, after a FAILED
  // read, or for a copy set without one — and then NO SAVE GOES OUT (#1359):
  // `Storage` recorded no version for the key, so the write would carry no
  // precondition and could overwrite newer settings it never saw.
  const [storedBookingConfig, setStoredBookingConfig] = useState<Record<string, unknown> | null>(
    null,
  );
  // Replace the held copy. `stored` is the document it came from, when the
  // caller read one: the loader passes it, and so does the court-state command,
  // whose copy IS the server's stored document. Without it nothing is known
  // about what is stored, an earlier answer must not survive the copy it
  // described, and saving is refused until a read answers (#1359).
  const setBookingConfig = useCallback(
    (next: SetStateAction<BookingConfig>, stored?: Record<string, unknown> | null) => {
      setHeldBookingConfig(next);
      setStoredBookingConfig(stored && typeof stored === 'object' ? stored : null);
    },
    [],
  );
  // Whether the STORED config carries opening hours — no booking opens before
  // it does (owner decision 2026-10-31, #1192). Null until the loader has
  // answered, so nothing is claimed about a document nobody has read yet.
  const [bookingHoursSaved, setBookingHoursSaved] = useState<boolean | null>(null);
  // Nothing stored until a club sets a guest price (#833) — never an invented one.
  const [bookingGuestPrices, setHeldBookingGuestPrices] = useState<BookingGuestPrices | null>(null);
  // Whether the guest prices were read (#1362) — the zones' rule, for the same
  // reason: `null` is also what a failed read leaves.
  const [bookingGuestPricesRead, setBookingGuestPricesRead] = useState(false);
  // A SAVE OF ONE OF THEM FAILED, and that document is unread again (#1370).
  // Only so the screen can say WHY it must be re-read — a failed save, not a
  // failed read. Cleared when «Last inn på nytt» has read every document.
  const [bookingSaveFailed, setBookingSaveFailed] = useState(false);
  const setBookingGuestPrices = useCallback(
    (next: SetStateAction<BookingGuestPrices | null>, read = false) => {
      setHeldBookingGuestPrices(next);
      setBookingGuestPricesRead(read);
    },
    [],
  );

  // Price-zone CRUD — admin edits the whole list at once from the editor modal.
  // Storage-first (HANDOVER §13): await Storage.set BEFORE setState.
  const updateBookingZones = async (
    newZones: BookingZone[],
  ): Promise<BookingSettingsUpdateResult> => {
    // NOTHING GOES OUT OVER ZONES NOBODY READ (#1362), for any caller.
    if (!bookingZonesRead) {
      return { success: false, error: BOOKING_ZONES_NOT_READ, code: BOOKING_ZONES_NOT_READ };
    }
    try {
      await Storage.set('booking_zones_v1', JSON.stringify(newZones));
      setBookingZones(newZones, true);
      return { success: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre pris-soner:', e);
      // UNREAD AGAIN (#1370): `Storage` holds the key as must-refresh, so a
      // save would be refused every time; unread, the screen offers the re-read.
      // The held zones stay as they were (storage-first).
      setBookingZonesRead(false);
      setBookingSaveFailed(true);
      return writeFailed(e);
    }
  };

  // Booking config (buffer rule, opening hours, durations, …). Storage-first.
  //
  // `changes` IS MERGED INTO THE DOCUMENT THIS CLIENT HOLDS, never written as
  // the whole document (#1340). Two screens write `booking_config_v1`: the
  // settings dialog sends the fields it drafts, and the price-zone editor owns
  // `defaultPricePerHour`. Written whole, the dialog's draft erased the default
  // price on every unrelated save, and the minutes no zone covers became free.
  // Each writer passes the fields it owns; everything else is kept as read.
  //
  // The merge is onto the STORED document this client read, not the held copy
  // with the engine defaults filled in (#1357): a save adds only the fields its
  // screen sends, never a default nobody edited. The held copy gets the same
  // fields, so the screens see exactly what they did before.
  //
  // Onto the copy READ, not a fresh read: the copy is what the version
  // token in `Storage` was recorded against, so a save built on it is refused
  // (409) if someone else wrote meanwhile. Re-reading first would refresh the
  // token and turn that refusal into a silent overwrite. A shallow merge: each
  // top-level field has one owner (`courtMeta[*].outOfService`, inside the
  // dialog's `courtMeta`, is the server's — routes/kv.ts re-applies it).
  // docs/architecture/DOMAIN_RULES.md § 7 has the ownership table.
  const updateBookingConfig = async (
    changes: BookingConfig,
  ): Promise<BookingSettingsUpdateResult> => {
    // NOTHING GOES OUT OVER A DOCUMENT NOBODY READ (#1359). Refused here, for
    // every caller, before `Storage` is touched; the screens also say so up
    // front and offer the re-read below.
    if (storedBookingConfig === null) {
      return { success: false, error: BOOKING_CONFIG_NOT_READ, code: BOOKING_CONFIG_NOT_READ };
    }
    const newConfig = { ...bookingConfig, ...changes };
    const newStored = { ...storedBookingConfig, ...changes };
    try {
      await Storage.set('booking_config_v1', JSON.stringify(newStored));
      setBookingConfig(newConfig, newStored);
      // What was just stored is the document now.
      setBookingHoursSaved(hasSavedBookingHours(newStored));
      return { success: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre booking-konfig:', e);
      // UNREAD AGAIN (#1370), as for the zones: the stored base is forgotten
      // (the held copy is kept), so the next save waits for a re-read, which
      // brings back the document and a version to save it against.
      setStoredBookingConfig(null);
      setBookingSaveFailed(true);
      return writeFailed(e);
    }
  };

  // A SERVER COMMAND wrote the venue setup and handed back what it stored —
  // the court-state command (#271). That copy IS the stored document, so it
  // becomes the held copy and the base the next save merges into (#1357). Not a
  // save: nothing is written from here.
  //
  // IT DOES NOT COUNT AS A READ (#1359). The command's answer carries no
  // version, so `Storage` still has only what the mount read recorded. After a
  // read that answered, that is the old version, and the next save is refused
  // 409 rather than overwriting — as before. After a read that FAILED it is
  // nothing, and a save would go out unconditionally; so an unread setup stays
  // unread until «Last inn på nytt» answers.
  const applyServerBookingConfig = useCallback((stored: Record<string, unknown>) => {
    setHeldBookingConfig(stored);
    setStoredBookingConfig((prev) => (prev === null ? null : stored));
  }, []);

  // «Last inn på nytt» (#1359, #1362): read the booking settings that are
  // UNREAD again, the way the loader does — the venue setup, the zones, the
  // guest prices. A read that answers (the document, or that there is none)
  // records its version in `Storage`, so the next save carries a precondition,
  // and unlocks saving that document. A read that fails again changes nothing.
  //
  // ONLY the unread ones. A document this client holds was read at a version,
  // and an editor may still hold a draft built on it; re-reading would refresh
  // the token under that older copy and turn a 409 into a silent overwrite.
  // Succeeds only when every document has now been read.
  const reloadBookingConfig = async (): Promise<BookingSettingsUpdateResult> => {
    let refused: BookingNotReadCode | null = null;
    if (storedBookingConfig === null) {
      const read = await readBookingConfig(BOOKING_DEFAULT_CONFIG);
      if (read.stored === null) refused = BOOKING_CONFIG_NOT_READ;
      else {
        setBookingConfig(read.config, read.stored);
        setBookingHoursSaved(read.hoursSaved);
      }
    }
    if (!bookingZonesRead) {
      const read = await readBookingZones();
      if (!read.read) refused ??= BOOKING_ZONES_NOT_READ;
      else setBookingZones(read.zones, true);
    }
    if (!bookingGuestPricesRead) {
      const read = await readBookingGuestPrices();
      if (!read.read) refused ??= BOOKING_GUEST_PRICES_NOT_READ;
      else setBookingGuestPrices(read.guestPrices, true);
    }
    if (refused) return { success: false, error: refused, code: refused };
    setBookingSaveFailed(false);
    return { success: true };
  };

  // The guest default price (#833). Storage-first, like the two above.
  const updateBookingGuestPrices = async (
    next: BookingGuestPrices,
  ): Promise<BookingSettingsUpdateResult> => {
    // Nothing goes out over guest prices nobody read (#1362).
    if (!bookingGuestPricesRead) {
      return {
        success: false,
        error: BOOKING_GUEST_PRICES_NOT_READ,
        code: BOOKING_GUEST_PRICES_NOT_READ,
      };
    }
    try {
      await Storage.set('booking_guest_prices_v1', JSON.stringify(next));
      setBookingGuestPrices(next, true);
      return { success: true };
    } catch (e) {
      console.error('[Padel24] Kunne ikke lagre gjestepriser:', e);
      // Unread again (#1370), as for the zones.
      setBookingGuestPricesRead(false);
      setBookingSaveFailed(true);
      return writeFailed(e);
    }
  };

  return {
    bookingZones,
    bookingConfig,
    bookingGuestPrices,
    setBookingGuestPrices,
    updateBookingGuestPrices,
    // Exposed so the monolith's atomic mount loader can flush the loaded values
    // (these go away once the loader moves into StoreProvider).
    // `setBookingConfig(held, stored)` — see above for the second argument.
    setBookingZones,
    setBookingConfig,
    bookingHoursSaved,
    setBookingHoursSaved,
    updateBookingZones,
    updateBookingConfig,
    // False until the venue setup has been read (or found absent): saving it is
    // refused meanwhile (#1359), and the booking screen says so.
    bookingConfigRead: storedBookingConfig !== null,
    // False until the price zones / the guest prices have been read (or found
    // absent): saving them is refused meanwhile (#1362).
    bookingZonesRead,
    bookingGuestPricesRead,
    // True after a save of one of them failed (#1370), until the re-read has
    // read them all: the screen says a save did not go through.
    bookingSaveFailed,
    reloadBookingConfig,
    applyServerBookingConfig,
  };
}
